/**
 * Roster Entry — the v1 home for under-13 ghost players (see
 * docs/spec/22a-v1-launch-posture.md + migration 00084).
 *
 * A roster_entry is a player slot on a team that doesn't have its
 * own User row. Coaches CRUD them; ratings + dev tracking reference
 * the roster_entry directly until the kid claims a User account
 * (v2, sets `claimed_user_id`).
 *
 * This module is presentation-agnostic: types + a single
 * validation function. CRUD lives in app workspaces (web2 + mobile
 * + admin) so each tier owns its own Supabase client + UI flow.
 */

import { ageInYears } from '../auth/ukGdprGate';

/**
 * One row from `roster_entries`. Matches migration 00084 1:1.
 */
export interface RosterEntry {
  id: string;
  team_id: string;
  first_name: string;
  last_name: string;
  /** ISO date (YYYY-MM-DD), or a Date the caller already parsed. */
  dob: string;
  position: string | null;
  jersey_number: number | null;
  parent_user_id: string | null;
  /** Set at v2 when an under-13 kid claims their own User account. */
  claimed_user_id: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Shape callers pass to "add a new roster entry" — strict subset of
 * RosterEntry with all server-managed fields stripped.
 */
export interface RosterEntryInput {
  team_id: string;
  first_name: string;
  last_name: string;
  /** ISO date string (YYYY-MM-DD). */
  dob: string;
  position?: string | null;
  jersey_number?: number | null;
  parent_user_id?: string | null;
  notes?: string | null;
}

/** Discriminated validation result. */
export type RosterEntryValidationResult =
  | { ok: true; input: RosterEntryInput }
  | { ok: false; errors: RosterEntryValidationError[] };

export interface RosterEntryValidationError {
  field: keyof RosterEntryInput;
  message: string;
}

const MAX_NAME_LEN = 80;
const MAX_NOTES_LEN = 1000;
const MAX_JERSEY = 99;
const MIN_JERSEY = 0;

/**
 * Validate a roster-entry input before sending to the API.
 *
 * Rules:
 *   - first_name, last_name: required, ≤ 80 chars, non-whitespace
 *   - dob: required, valid ISO date, not in the future, reasonable
 *     (no DoBs > 120 years ago — almost certainly a typo)
 *   - position: optional, ≤ 40 chars
 *   - jersey_number: optional, integer 0–99
 *   - notes: optional, ≤ 1000 chars
 *
 * Does NOT validate `team_id` / `parent_user_id` shape (Supabase RLS
 * and FK constraints handle invalid IDs server-side).
 */
export function validateRosterEntry(
  input: RosterEntryInput,
  refDate: Date = new Date(),
): RosterEntryValidationResult {
  const errors: RosterEntryValidationError[] = [];

  if (!input.first_name?.trim()) {
    errors.push({ field: 'first_name', message: 'First name is required.' });
  } else if (input.first_name.length > MAX_NAME_LEN) {
    errors.push({ field: 'first_name', message: `First name must be ${MAX_NAME_LEN} characters or fewer.` });
  }

  if (!input.last_name?.trim()) {
    errors.push({ field: 'last_name', message: 'Last name is required.' });
  } else if (input.last_name.length > MAX_NAME_LEN) {
    errors.push({ field: 'last_name', message: `Last name must be ${MAX_NAME_LEN} characters or fewer.` });
  }

  if (!input.dob) {
    errors.push({ field: 'dob', message: 'Date of birth is required.' });
  } else {
    const dobDate = parseIsoDate(input.dob);
    if (!dobDate) {
      errors.push({ field: 'dob', message: "Date of birth must be a valid date (YYYY-MM-DD)." });
    } else if (dobDate.getTime() > refDate.getTime()) {
      errors.push({ field: 'dob', message: 'Date of birth cannot be in the future.' });
    } else {
      const age = ageInYears(dobDate, refDate);
      if (age > 120) {
        errors.push({ field: 'dob', message: 'Date of birth is unrealistically old — check it.' });
      }
    }
  }

  if (input.position != null && input.position.length > 40) {
    errors.push({ field: 'position', message: 'Position must be 40 characters or fewer.' });
  }

  if (input.jersey_number != null) {
    if (!Number.isInteger(input.jersey_number)) {
      errors.push({ field: 'jersey_number', message: 'Jersey number must be a whole number.' });
    } else if (input.jersey_number < MIN_JERSEY || input.jersey_number > MAX_JERSEY) {
      errors.push({ field: 'jersey_number', message: `Jersey number must be between ${MIN_JERSEY} and ${MAX_JERSEY}.` });
    }
  }

  if (input.notes != null && input.notes.length > MAX_NOTES_LEN) {
    errors.push({ field: 'notes', message: `Notes must be ${MAX_NOTES_LEN} characters or fewer.` });
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, input };
}

/**
 * Compute the current age of a roster entry from its `dob` field.
 * Helpful for UI sorting / under-13 chips. Returns null if the dob
 * doesn't parse.
 */
export function rosterEntryAge(
  entry: Pick<RosterEntry, 'dob'>,
  refDate: Date = new Date(),
): number | null {
  const dob = parseIsoDate(entry.dob);
  if (!dob) return null;
  return ageInYears(dob, refDate);
}

/**
 * True when the entry's DoB places them under 13 today — i.e. the
 * "ghost player" cohort the table was added for. After v2 unlock,
 * these entries are the candidates to be invited to claim an account.
 */
export function isUnder13(
  entry: Pick<RosterEntry, 'dob'>,
  refDate: Date = new Date(),
): boolean {
  const age = rosterEntryAge(entry, refDate);
  return age != null && age < 13;
}

function parseIsoDate(s: string): Date | null {
  // Accept YYYY-MM-DD only — narrower than `new Date(s)` which is
  // lenient with locale-dependent parsing.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!match) return null;
  const [, y, mo, d] = match;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  // Reject impossible dates that JS would silently normalise
  // (e.g. 2020-02-31 → 2020-03-02).
  if (
    date.getFullYear() !== Number(y) ||
    date.getMonth() !== Number(mo) - 1 ||
    date.getDate() !== Number(d)
  ) {
    return null;
  }
  return date;
}
